import { useQueries, useQuery } from '@tanstack/react-query';
import { fetchAccessInviteLocations, fetchUpcomingInvites, type AccessInvite } from '../api';
import { UPCOMING_INVITES_QUERY_KEY } from '../accessInviteCache';
import { filterUpcomingInvites } from '../inviteFilters';
import { fetchGatePins, gatePinsQueryKey, type GatePin } from '../memberAppApi';
import { MEMBER_CONTACTS_QUERY_KEY, fetchMemberContacts } from '../memberContactsApi';
import { isoDay } from '../visitorAccessFormat';

export type BookingKind = 'delivery' | 'collection' | 'guest_stay' | 'goods_removal';

export const BOOKING_LABEL: Record<BookingKind, string> = {
  delivery: 'Delivery',
  collection: 'Collection',
  guest_stay: 'Guest stay',
  goods_removal: 'Goods removal',
};

/** A PIN the guard made at the gate, with its property so it can be cancelled there. */
export interface GuardPin extends GatePin {
  location_id: string;
}

/** One row of "Your PINs": a saved group's visit (`key` is the group and the day), a booking, or one person's PIN. */
export type YourPin =
  | { kind: 'group'; key: string; groupId: string; name: string; invites: AccessInvite[] }
  | { kind: 'booking'; booking: BookingKind; invite: AccessInvite }
  | { kind: 'person'; invite: AccessInvite; name: string; arrived: boolean };

/**
 * The member's live PINs, as Overview counts them and Active PINs lists them: the PINs the guard
 * made at the gate on each property whose gate keeps them, and the member's own.
 */
export function useActivePins() {
  const invitesQuery = useQuery({ queryKey: UPCOMING_INVITES_QUERY_KEY, queryFn: fetchUpcomingInvites });
  const peopleQuery = useQuery({ queryKey: MEMBER_CONTACTS_QUERY_KEY, queryFn: fetchMemberContacts });
  const locationsQuery = useQuery({ queryKey: ['access-invite-locations'], queryFn: fetchAccessInviteLocations });

  // 1. Only a gate that keeps its own PINs (features set) can list the ones the guard made.
  const gateLocations = (locationsQuery.data ?? []).filter((location) => location.features);
  const gatePinQueries = useQueries({
    queries: gateLocations.map(({ location_id }) => ({
      queryKey: gatePinsQueryKey(location_id),
      queryFn: () => fetchGatePins(location_id),
    })),
  });
  const guardPins: GuardPin[] = gatePinQueries.flatMap((query, index) =>
    (query.data ?? [])
      .filter((pin) => pin.linked_access_invite_id === null)
      .map((pin) => ({ ...pin, location_id: gateLocations[index]!.location_id })),
  );

  // 2. The member's own live PINs, a saved group's folded into one row per visit day, so the same
  //    group sent for two days is two rows.
  const people = peopleQuery.data ?? [];
  const yourPins: YourPin[] = [];
  for (const invite of filterUpcomingInvites(invitesQuery.data ?? [])) {
    const { booking, visitor_group_id: groupId, visitor_group_name: groupName } = invite.metadata ?? {};

    if (groupId) {
      const key = `${groupId}:${invite.valid_from ? isoDay(new Date(invite.valid_from)) : ''}`;
      const group = yourPins.find((row) => row.kind === 'group' && row.key === key);
      if (group?.kind === 'group') group.invites.push(invite);
      else yourPins.push({ kind: 'group', key, groupId, name: groupName ?? 'Group', invites: [invite] });
    } else if (booking && booking !== 'group') {
      yourPins.push({ kind: 'booking', booking, invite });
    } else {
      const person = people.find((row) => row.member_contact_id === invite.member_contact_id);
      const live = person?.live_invites.find((row) => row.access_invite_id === invite.access_invite_id);
      yourPins.push({
        kind: 'person',
        invite,
        name: person?.display_name ?? invite.visitor_name ?? 'Visitor',
        arrived: Boolean(live?.arrived_at),
      });
    }
  }

  const today = new Date().toDateString();

  return {
    guardPins,
    guardPinsToday: guardPins.filter((pin) => pin.created_at && new Date(pin.created_at).toDateString() === today).length,
    yourPins,
    isLoading: invitesQuery.isLoading || peopleQuery.isLoading,
    isError: invitesQuery.isError,
    refetch: () => Promise.all([invitesQuery.refetch(), ...gatePinQueries.map((query) => query.refetch())]),
  };
}
